import type { ResultOf, VariablesOf } from "gql.tada";
import type { CheckboxItem } from "interfaces/product/product.types";
import type { Dispatch, SetStateAction } from "react";
import React, { useCallback, useMemo, useState } from "react";
import { useMutation } from "urql";
import {
  createLCAProductReportMut,
  createProductReport,
  createProductXBIMReport,
} from "api/mutations";
import { getLCAProductReportQry } from "api/queries";
import { useDownloadModalCheckboxes, useTranslate } from "hooks";
import { useUser } from "stores/user";
import {
  fetchData,
  formatPresentation,
  getSelectedItems,
  handleMultipleFilesDownload,
  handleSingleFileDownload,
} from "utils";
import { getProductState } from "api/endpoints";
import type {
  CheckboxContent,
  DocumentsState,
  OrderCode,
} from "interfaces/product/product.types";
import { toast } from "react-toastify";
import { useLanguage } from "stores/globalStore";
import { Unit } from "variables/enums";
import {
  useProductInput,
  useProductOutput,
  type ProductOutput,
} from "stores/product";
import { useProjectUnit } from "stores/project";
import { Button } from "./Button";
import { Icon } from "./Icon/Icon";

type Props = {
  itemsData: CheckboxItem[];
  setItemsData: Dispatch<SetStateAction<CheckboxItem[]>>;
  documents: DocumentsState;
  orderCode: OrderCode;
  setExpandedAccordionIds: Dispatch<SetStateAction<string[]>>;
  nodeId: string;
};

export const ProductReportsDownloadButtonsBlock = ({
  itemsData,
  setItemsData,
  documents,
  orderCode,
  setExpandedAccordionIds,
  nodeId,
}: Props) => {
  const translate = useTranslate();
  const [reportsInProgress, setReportsInProgress] = useState(false);
  const lang = useLanguage();
  const user = useUser();

  const productInput = useProductInput();
  const productOutput = useProductOutput();
  const unit = useProjectUnit();

  const units = unit || Unit.de_m3ph;

  const exeGetLCAProductReport = useCallback(
    (variables: VariablesOf<typeof getLCAProductReportQry>) =>
      new Promise<ResultOf<typeof getLCAProductReportQry>>(
        (resolve, reject) => {
          fetchData({
            query: getLCAProductReportQry,
            variables,
            onSuccess: (data) => {
              resolve(data);
            },
            onError: (err) => {
              if (err.error) {
                toast.error(err.error.message, {
                  type: "error",
                  theme: "colored",
                });
              }

              setExpandedAccordionIds([]);
              reject(err);
            },
          });
        },
      ),
    [setExpandedAccordionIds],
  );

  const [, executeCreateProductReport] = useMutation(createProductReport);
  const [, executeCreateProductXBIMReport] = useMutation(
    createProductXBIMReport,
  );
  const [, exeCreateLCAProductReport] = useMutation(createLCAProductReportMut);

  const { handleSelectAll, handleClearAll } = useDownloadModalCheckboxes({
    setData: setItemsData,
    setExpandedAccordionIds,
    itemsData,
  });
  const resetCheckedItems = itemsData.map((item) => ({
    ...item,
    checked: false,
    checkboxContent: item.checkboxContent
      ? item.checkboxContent.map((subItem) => ({
          ...subItem,
          checked: false,
        }))
      : item.checkboxContent,
  }));

  const handleSubmit = useCallback(async () => {
    setReportsInProgress(true);
    const productData = await new Promise<ProductOutput>((resolve, reject) => {
      if (productOutput) {
        resolve(productOutput);
        return;
      }
      getProductState({
        input: {
          nodeId,
          language: lang,
          units,
          country: user!.tenant,
        },
        onSuccess: (data) => {
          if (data?.getProductState?.result) {
            resolve(data.getProductState.result);
          }
        },
        onError: (err) => {
          if (err.error) {
            toast.error(err.error.message, {
              type: "error",
              theme: "colored",
            });
          }

          setExpandedAccordionIds([]);
          setReportsInProgress(false);
          setItemsData(resetCheckedItems);
          reject(err);
        },
      });
    });
    if (productData) {
      const formattedPresentation =
        formatPresentation(productData?.product?.orderCode?.presentation) || "";
      const selectedItems = getSelectedItems(itemsData);
      if (selectedItems.length === 1) {
        await handleSingleFileDownload({
          item: selectedItems[0],
          productOutput: productData,
          presentation: formattedPresentation,
          units,
          lang,
          orderCode,
          executeCreateProductXBIMReport,
          executeCreateProductReport,
          executeCreateLCAProductReport: exeCreateLCAProductReport,
          executeGetLCAProductReport: exeGetLCAProductReport,
          documents,
          requestParams: productInput,
          country: user?.tenant,
          translate,
          nodeId,
        });
      } else {
        await handleMultipleFilesDownload({
          selectedItems,
          productOutput: productData,
          presentation: formattedPresentation,
          units,
          lang,
          orderCode,
          executeCreateProductXBIMReport,
          executeCreateProductReport,
          executeCreateLCAProductReport: exeCreateLCAProductReport,
          executeGetLCAProductReport: exeGetLCAProductReport,
          documents,
          requestParams: productInput,
          country: user?.tenant,
          translate,
          nodeId,
        });
      }
      setReportsInProgress(false);
      setItemsData(resetCheckedItems);
    }
  }, [
    productOutput,
    nodeId,
    lang,
    units,
    user,
    setExpandedAccordionIds,
    setItemsData,
    resetCheckedItems,
    itemsData,
    orderCode,
    executeCreateProductXBIMReport,
    executeCreateProductReport,
    exeCreateLCAProductReport,
    exeGetLCAProductReport,
    documents,
    productInput,
    translate,
  ]);

  const checked = useMemo(
    () =>
      itemsData.reduce<(CheckboxItem | CheckboxContent)[]>((acc, item) => {
        if (item.checked) {
          acc.push(item);
        }

        if (item.checkboxContent) {
          const checkedSublistItems = item.checkboxContent.filter(
            (subItem) => subItem.checked,
          );
          acc.push(...checkedSublistItems);
        }

        return acc;
      }, []),
    [itemsData],
  );

  return (
    <div className="d-flex justify-content-end">
      <Button
        data-testid="DownloadModal_select-all-btn"
        onClick={handleSelectAll}
        className="me-3"
        color="secondary"
        outline
      >
        {translate("SELECT_ALL")}
      </Button>
      <Button
        data-testid="DownloadModal_clear-selection-btn"
        onClick={handleClearAll}
        className="me-3"
        color="secondary"
        outline
        disabled={!checked.length}
      >
        {translate("CLEAR_SELECTION")}
      </Button>
      <Button
        data-testid="ProductReportsDownload-btn"
        className="d-inline-block text-white"
        onClick={handleSubmit}
        color="primary"
        disabled={checked.length === 0 || reportsInProgress}
        isLoading={reportsInProgress}
      >
        <Icon iconName="arrow-down-bracket-regular" className="me-1" />
        {translate("DOWNLOAD_DATA")}
      </Button>
    </div>
  );
};
